/* eslint-disable react-hooks/exhaustive-deps */
"use client";
import React, { useState, useRef, useEffect } from "react";
import QueryString from "qs";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";
import Link from "next/link";

import { toWebp, getArticleRoutePrefix } from "@/utils/common";
import styles from "./blog-post.module.scss";

interface Props {
  data: any;
}

interface FilterOption {
  id: number;
  name: string;
  slug: string;
}

interface SelectedFilters {
  category: string[];
  industry: string[];
  blogType: string[];
}

const initValue: SelectedFilters = {
  category: [],
  industry: [],
  blogType: [],
};
const BlogPost: React.FC<Props> = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [, setUrl] = useState("");
  const [search] = useState("");
  const [debouncedText, setDebouncedText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isMoreLoading, setIsMoreLoading] = useState(false);
  const [isScrollUp, setIsScrollUp] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(true);
  const [isIndustryOpen, setIsIndustryOpen] = useState(false);
  const [isContentTypeOpen, setIsContentTypeOpen] = useState(false);
  const [categories, setCategories] = useState<FilterOption[]>([]);
  const [blogIndustries, setBlogIndustries] = useState<FilterOption[]>([]);
  const [blogTypes, setBlogTypes] = useState<FilterOption[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>(initValue);
  const [allArticles, setAllArticles] = useState<any[]>([]);
  const [totalArticles, setTotalArticles] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const baseAPIUrl = process.env.NEXT_PUBLIC_REACT_APP_API_BASE_URL || "";
  const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL || "";
  const activeTab = "home";

  // Scroll listing into view after filter change
  useEffect(() => {
    const element = document.getElementById("post");
    if (element && isScrollUp) {
      window.scrollTo({ top: element.offsetTop - 300, behavior: "instant" });
      setIsScrollUp(false);
    }
  }, [isScrollUp]);

  // Detect a real mouse on touch-capable devices (iPad/mobile with an
  // external mouse or trackpad connected). `(any-hover: hover)` /
  // `(any-pointer: fine)` are unreliably reported by mobile/tablet browsers
  // once a mouse is attached, so real pointer type is checked directly via
  // the Pointer Events API instead, and `.mouse-connected` is toggled on
  // <html> so the blog card CSS can switch from "always show" to
  // "reveal on hover" (see globals.css).
  useEffect(() => {
    const isTouchDevice =
      typeof window !== "undefined" &&
      ("ontouchstart" in window || navigator.maxTouchPoints > 0);
    if (!isTouchDevice) return;

    const handlePointerMove = (e: PointerEvent) => {
      if (e.pointerType === "mouse") {
        document.documentElement.classList.add("mouse-connected");
      } else if (e.pointerType === "touch") {
        document.documentElement.classList.remove("mouse-connected");
      }
    };
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerdown", handlePointerMove);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerdown", handlePointerMove);
    };
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: { target: any }) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenFilter(null);
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [searchParams]);

  // Lock body scroll when mobile filter is open
  useEffect(() => {
    if (isFilterOpen) document.body.classList.add("overflow-hidden");
    else document.body.classList.remove("overflow-hidden");
  }, [isFilterOpen]);

  // Fetch filter options from API on mount
  useEffect(() => {
    const fetchFilterOptions = async (
      endpoint: string,
      labelField: string,
    ): Promise<FilterOption[]> => {
      try {
        const res = await fetch(
          `${baseAPIUrl}${endpoint}?pagination[pageSize]=100`,
        );
        const json = await res.json();
        return (json?.data ?? []).map((item: any) => ({
          id: item.id,
          name: item.attributes?.[labelField] ?? "",
          slug: item.attributes?.slug ?? "",
        }));
      } catch {
        return [];
      }
    };
    Promise.all([
      fetchFilterOptions("categories", "name"),
      fetchFilterOptions("blog-industries", "tag"),
      fetchFilterOptions("blog-types", "tag"),
    ]).then(([cats, industries, types]) => {
      setCategories(cats);
      setBlogIndustries(industries);
      setBlogTypes(types);
    });
  }, [baseAPIUrl]);

  // Apply hash/query filter whenever the URL changes, then clean URL
  useEffect(() => {
    if (blogTypes.length === 0) return;

    // Parse category from hash (#category=whitepaper) or query (?category=whitepaper)
    const asPath = router.asPath;
    const hashIndex = asPath.indexOf("#");
    const queryIndex = asPath.indexOf("?");
    const hashStr = hashIndex !== -1 ? asPath.slice(hashIndex + 1) : "";
    const queryStr = queryIndex !== -1 ? asPath.slice(queryIndex + 1) : "";
    const category =
      new URLSearchParams(hashStr).get("category") ||
      new URLSearchParams(queryStr).get("category");

    if (!category) return;

    const match = blogTypes.find(
      (bt) =>
        bt.slug.toLowerCase() === category.toLowerCase() ||
        bt.name.toLowerCase() === category.toLowerCase(),
    );
    if (match) {
      setSelectedFilters((prev) => ({ ...prev, blogType: [match.slug] }));
    }
    router.replace("/blog", undefined, { shallow: true });
  }, [blogTypes, router.asPath]);

  // Initial articles fetch
  useEffect(() => {
    fetchAPI(0, false);
  }, []);

  // Refetch when filters change
  useEffect(() => {
    fetchAPI(0, false);
  }, [selectedFilters]);

  // Load more pages
  useEffect(() => {
    if (currentPage > 0) fetchAPI(currentPage, true);
  }, [currentPage]);

  // Debounced search
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedText(search?.trim()), 1000);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    if (!!debouncedText) fetchAPI(0, false);
  }, [debouncedText]);

  const fetchAPI = async (page = 0, isLoadMore = false) => {
    try {
      if (!isLoadMore) setCurrentPage(0);
      const filterArray: any[] = [{ slug: { $notIn: ["blog", "newsroom"] } }];

      if (search?.trim()) {
        filterArray.push({ title: { $containsi: search.trim() } });
      }

      if (selectedFilters.category.length > 0) {
        filterArray.push({
          categories: { slug: { $in: selectedFilters.category } },
        });
      }

      if (selectedFilters.industry.length > 0) {
        filterArray.push({
          blog_industries: { slug: { $in: selectedFilters.industry } },
        });
      }

      if (selectedFilters.blogType.length > 0) {
        filterArray.push({
          blog_types: { slug: { $in: selectedFilters.blogType } },
        });
      }

      const query = QueryString.stringify(
        {
          filters: { $and: filterArray },
          populate: "deep,5",
          sort: { publish_at: "DESC" },
          pagination: { page: page + 1, pageSize: 12 },
        },
        { encodeValuesOnly: true },
      );
      const url = `${baseAPIUrl}articles?${decodeURIComponent(query)}`;
      setUrl(url);
      callAPI(url, isLoadMore);
    } catch (error) {
      console.log("fetchAPI ~ error:", error);
    }
  };

  const callAPI = async (path: string, isLoadMore = false) => {
    try {
      if (isLoadMore) setIsMoreLoading(true);
      else setIsLoading(true);

      if (!isLoadMore) setIsScrollUp(true);

      const apiRes = await fetch(path);
      const apiResData = await apiRes.json();
      if (isLoadMore) {
        setAllArticles((prev) => [...prev, ...(apiResData?.data || [])]);
      } else {
        setAllArticles(apiResData?.data || []);
      }
      setTotalArticles(apiResData?.meta?.pagination?.total || 0);
    } catch (error) {
      console.log("callAPI ~ error:", error);
      setAllArticles([]);
    } finally {
      setIsLoading(false);
      setIsMoreLoading(false);
    }
  };

  // Multi-select toggle: clicking a slug adds it to the array, clicking again removes it
  const handleMultiSelect = (key: keyof SelectedFilters, slug: string) => {
    setSelectedFilters((prev) => {
      const current = prev[key] as string[];
      const exists = current.includes(slug);
      return {
        ...prev,
        [key]: exists ? current.filter((s) => s !== slug) : [...current, slug],
      };
    });
  };

  const activeFilterCount =
    selectedFilters.category.length +
    selectedFilters.industry.length +
    selectedFilters.blogType.length;

  return (
    <div className="bg-primary-text min-h-[400px]">
      {/* Filter Bar */}
      <div
        className="sticky pt-[0px] md:pt-[0px] top-[80px] md:border border-t-blue-100 bg-primary-text z-30"
        ref={dropdownRef}
      >
        <div className="container">
          <div className="md:block hidden">
            <div className="grid grid-cols-1 md:grid-cols-3">
              {/* Topic Filter */}
              <div className="md:pr-[30px] md:pl-[30px] first:pl-0 flex flex-col border-b md:border-b-0 md:border-r border-blue-100">
                <FilterHeader
                  title="Topic"
                  count={selectedFilters.category.length}
                  open={openFilter === "topic"}
                  onClick={() =>
                    setOpenFilter(openFilter === "topic" ? null : "topic")
                  }
                />
              </div>

              {/* Industry Filter */}
              <div className="md:pr-[30px] md:pl-[30px] first:pl-0 flex flex-col border-b md:border-b-0 md:border-r border-blue-100">
                <FilterHeader
                  title="Industry"
                  count={selectedFilters.industry.length}
                  open={openFilter === "industry"}
                  onClick={() =>
                    setOpenFilter(openFilter === "industry" ? null : "industry")
                  }
                />
              </div>

              {/* Content Type Filter */}
              <div className="md:pr-[30px] md:pl-[30px] first:pl-0 flex-col flex">
                <FilterHeader
                  title="Content Type"
                  count={selectedFilters.blogType.length}
                  open={openFilter === "content"}
                  onClick={() =>
                    setOpenFilter(openFilter === "content" ? null : "content")
                  }
                />
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Filter */}
        <div className="md:hidden block">
          <button
            onClick={() => setIsFilterOpen(true)}
            className="w-full h-[54px] border border-blue-100 bg-primary-text px-[32px] flex items-center justify-between cursor-pointer"
          >
            <span className="text-white text-[18px] font-regular flex items-center gap-2">
              Filter
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center bg-[#7EAB00] text-white text-[11px] font-bold rounded-full w-[20px] h-[20px]">
                  {activeFilterCount}
                </span>
              )}
            </span>

            <div className="relative w-[24px] h-[24px]">
              <Image
                src="/images/mobile-plus.svg"
                alt="open"
                fill
                className="object-contain"
              />
            </div>
          </button>
        </div>

        {/* Desktop Dropdowns */}
        <div
          className={`hidden md:block absolute top-[calc(100%+1px)] left-0 right-0 z-50 bg-primary-text transition-all duration-500 origin-top transform ${openFilter
            ? "scale-y-100 opacity-100 visible"
            : "scale-y-95 opacity-0 invisible"
            }`}
        >
          <div className="bg-primary-text">
            {openFilter === "topic" && (
              <DropdownContent
                items={categories}
                selectedSlugs={selectedFilters.category}
                onItemClick={(slug) => handleMultiSelect("category", slug)}
                onClear={() =>
                  setSelectedFilters((prev) => ({ ...prev, category: [] }))
                }
                onShow={() => setOpenFilter(null)}
              />
            )}
            {openFilter === "industry" && (
              <DropdownContent
                items={blogIndustries}
                selectedSlugs={selectedFilters.industry}
                onItemClick={(slug) => handleMultiSelect("industry", slug)}
                onClear={() =>
                  setSelectedFilters((prev) => ({ ...prev, industry: [] }))
                }
                onShow={() => setOpenFilter(null)}
              />
            )}
            {openFilter === "content" && (
              <DropdownContent
                items={blogTypes}
                selectedSlugs={selectedFilters.blogType}
                onItemClick={(slug) => handleMultiSelect("blogType", slug)}
                onClear={() =>
                  setSelectedFilters((prev) => ({ ...prev, blogType: [] }))
                }
                onShow={() => setOpenFilter(null)}
              />
            )}
          </div>
        </div>
      </div>

      {/* Blog Listing Section */}
      <section className="bg-primary-text relative z-0 py-[40px] lg:py-[70px] xl:py-[80px] overflow-hidden ">
        <div className="container">
          {isLoading && allArticles.length === 0 ? (
            <div className="flex justify-center py-20">
              <Image
                src="/images/bouncing-squares.svg"
                alt="loader"
                width={100}
                height={100}
              />
            </div>
          ) : allArticles?.length > 0 || isMoreLoading ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-[40px] justify-items-center">
                {allArticles.map((item: any) => (
                  <BlogCard
                    key={item.id}
                    item={item}
                    baseUrl={baseUrl}
                    router={router}
                    activeTab={activeTab}
                  />
                ))}
                {isMoreLoading &&
                  Array.from({ length: 12 }).map((_, i) => (
                    <BlogCardSkeleton key={i} />
                  ))}
              </div>

              {/* Load More Button */}
              {allArticles.length < totalArticles && (
                <div className="flex justify-center mt-[40px] md:mt-[60px] xl:mt-[80px]">
                  <button
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    disabled={isMoreLoading || isLoading}
                    className="cursor-pointer bg-text-button hover:bg-blue-700 transition-all duration-300 p-[12px_20px] text-white text-subtitle-3 font-semibold flex items-center gap-2 group"
                  >
                    {isMoreLoading ? "Loading..." : "Load More"}
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="my-[3px] flex-[0_0_auto]"
                    >
                      <path
                        d="M1.25 9.6582H17.75"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="square"
                        strokeLinejoin="round"
                        className="group-hover:opacity-100 opacity-0  transition-all duration-300"
                      ></path>
                      <path
                        d="M11.5908 2.5L18.7499 9.65909L11.5908 16.8181"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="square"
                        strokeLinejoin="round"
                        className="group-hover:translate-x-0 -translate-x-[3px] -translate-x-0 transition-all duration-300"
                      ></path>
                    </svg>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <h3 className="text-white text-heading-3">No results found</h3>
              <p className="text-gray-400 mt-4">
                Try adjusting your filters or search query
              </p>
            </div>
          )}
        </div>

        {/* Decorative Shapes */}
        <div className="banner-shape-1 absolute md:bottom-[-220px] bottom-[-200px] md:left-0 left-[-250px]  z-[-1] md:w-[60%]">
          <Image
            src="/images/blog-layer-blue.png"
            alt="Banner Shape"
            width={1262}
            height={434}
            className="object-cover w-[1262px] h-[434px]"
            quality={100}
          />
        </div>
        <div className="banner-shape-1 absolute md:bottom-[-260px] bottom-[-300px] md:right-0 z-[-1] right-[-300px] md:w-[60%]">
          <Image
            src="/images/blog-layer-green.png"
            alt="Banner Shape"
            width={1262}
            height={540}
            quality={100}
            className="object-cover lg:w-[1262px] h-[540px]"
          />
        </div>
      </section>
      {/* Mobile Filter Sidebar */}
      <div
        className={`fixed inset-0 top-[80px] z-[999] bg-black transition-all duration-300 ${isFilterOpen
          ? "opacity-100 visible translate-y-0"
          : "opacity-0 invisible translate-y-full"
          }`}
      >
        {/* Header */}
        <div className="w-full border border-blue-100 bg-primary-text px-[24px] py-[20px] flex items-center justify-between">
          <h2 className="text-white text-[16px] font-semibold">Filter</h2>

          <button onClick={() => setIsFilterOpen(false)}>
            <div className="relative w-[32px] h-[32px] cursor-pointer">
              <Image
                src="/images/mobile-close.svg"
                alt="close"
                fill
                className="object-contain"
              />
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="h-[calc(100vh-80px)] overflow-y-auto p-[24px] pb-[240px]">
          {/* TOPIC */}
          <div className="border border-blue-100 p-[16px]">
            <button
              onClick={() => setIsCategoriesOpen(!isCategoriesOpen)}
              className="w-full flex items-center justify-between cursor-pointer"
            >
              <h3 className="text-white text-[16px] font-semibold">Topic</h3>

              <Image
                src={
                  isCategoriesOpen
                    ? "/images/mobile-minus.svg"
                    : "/images/mobile-plus.svg"
                }
                alt="toggle"
                width={20}
                height={20}
              />
            </button>

            {isCategoriesOpen && (
              <div className="mt-[16px]">
                <div className="flex flex-wrap gap-[8px]">
                  {categories.map((item) => {
                    const isSelected = selectedFilters.category.includes(item.slug);
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleMultiSelect("category", item.slug)}
                        className={`px-[12px] py-[8px] text-[14px] border transition-all duration-300 flex items-center gap-[6px] ${isSelected
                          ? "bg-[#7EAB00] border-[#7EAB00] text-white"
                          : "bg-[#32343B] border-[#464953] text-gray-200"
                          }`}
                      >
                        {isSelected && (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M1.5 6L4.5 9L10.5 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                        {item.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* INDUSTRY */}
          <div className="border border-blue-100 mt-[20px] p-[16px]">
            <button
              onClick={() => setIsIndustryOpen(!isIndustryOpen)}
              className="w-full flex items-center justify-between cursor-pointer"
            >
              <h3 className="text-white text-[16px] font-semibold">Industry</h3>
              <Image
                src={isIndustryOpen ? "/images/mobile-minus.svg" : "/images/mobile-plus.svg"}
                alt="toggle"
                width={20}
                height={20}
              />
            </button>
            {isIndustryOpen && (
              <div className="mt-[16px]">
                <div className="flex flex-wrap gap-[8px]">
                  {blogIndustries.map((item) => {
                    const isSelected = selectedFilters.industry.includes(item.slug);
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleMultiSelect("industry", item.slug)}
                        className={`px-[12px] py-[8px] text-[14px] border transition-all duration-300 flex items-center gap-[6px] ${isSelected
                          ? "bg-[#7EAB00] border-[#7EAB00] text-white"
                          : "bg-[#32343B] border-[#464953] text-gray-200"
                          }`}
                      >
                        {isSelected && (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M1.5 6L4.5 9L10.5 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                        {item.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* CONTENT TYPE */}
          <div className="border border-blue-100 mt-[20px] p-[16px]">
            <button
              onClick={() => setIsContentTypeOpen(!isContentTypeOpen)}
              className="w-full flex items-center justify-between cursor-pointer"
            >
              <h3 className="text-white text-[16px] font-semibold">Content Type</h3>
              <Image
                src={isContentTypeOpen ? "/images/mobile-minus.svg" : "/images/mobile-plus.svg"}
                alt="toggle"
                width={20}
                height={20}
              />
            </button>
            {isContentTypeOpen && (
              <div className="mt-[16px]">
                <div className="flex flex-wrap gap-[8px]">
                  {blogTypes.map((item) => {
                    const isSelected = selectedFilters.blogType.includes(item.slug);
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleMultiSelect("blogType", item.slug)}
                        className={`px-[12px] py-[8px] text-[14px] border transition-all duration-300 flex items-center gap-[6px] ${isSelected
                          ? "bg-[#7EAB00] border-[#7EAB00] text-white"
                          : "bg-[#32343B] border-[#464953] text-gray-200"
                          }`}
                      >
                        {isSelected && (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M1.5 6L4.5 9L10.5 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                        {item.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="fixed bottom-0 left-0 right-0 bg-background-dark px-[24px] py-[50px] flex items-center justify-between shadow-[0_-10px_20px_rgba(0,0,0,0.5)]">
          <button
            onClick={() => {
              setSelectedFilters(initValue);
              setIsFilterOpen(false);
            }}
            className="bg-transparent transition-all duration-300 p-[12px_20px] text-white text-[16px] font-semibold flex items-center gap-2 group"
          >
            Clear all
            <svg
              width="16"
              height="16"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="mb-1 flex-[0_0_auto]"
            >
              <path
                d="M1.25 9.6582H17.75"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="square"
                strokeLinejoin="round"
                className="group-hover:opacity-100 opacity-0 transition-all duration-300"
              />
              <path
                d="M11.5908 2.5L18.7499 9.65909L11.5908 16.8181"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="square"
                strokeLinejoin="round"
                className="group-hover:translate-x-0 -translate-x-[3px] transition-all duration-300"
              />
            </svg>
          </button>

          <button
            onClick={() => setIsFilterOpen(false)}
            className="bg-text-button hover:bg-blue-700 transition-all duration-300 p-[12px_20px] text-white text-[16px] font-semibold flex items-center gap-2 group"
          >
            Show Results
            <svg
              width="16"
              height="16"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="my-[3px] flex-[0_0_auto]"
            >
              <path
                d="M1.25 9.6582H17.75"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="square"
                strokeLinejoin="round"
                className="group-hover:opacity-100 opacity-0 transition-all duration-300"
              />
              <path
                d="M11.5908 2.5L18.7499 9.65909L11.5908 16.8181"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="square"
                strokeLinejoin="round"
                className="group-hover:translate-x-0 -translate-x-[3px] transition-all duration-300"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper Components
function FilterHeader({
  title,
  open,
  onClick,
  count = 0,
}: {
  title: string;
  open: boolean;
  onClick: () => void;
  count?: number;
}) {
  return (
    <button
      onClick={onClick}
      className="cursor-pointer w-full h-[64px] md:h-[76px] flex items-center justify-between transition-all duration-300 group"
    >
      <span className="text-white text-subtitle-2 font-regular flex items-center gap-2">
        {title}
        {count > 0 && (
          <span className="inline-flex items-center justify-center bg-[#7EAB00] text-white text-[10px] font-bold rounded-full w-[18px] h-[18px] leading-none">
            <span className="mt-[2px]"> {count}</span>
          </span>
        )}
      </span>
      <div
        className={`transition-all duration-500 ease-out ${open ? "rotate-180" : ""}`}
      >
        {open ? (
          <Image
            src="/images/new-minus.svg"
            alt="minus"
            width={24}
            height={24}
            className=""
            unoptimized
          />
        ) : (
          <Image
            src="/images/new-plus.svg"
            alt="plus"
            width={24}
            height={24}
            className=""
            unoptimized
          />
        )}
      </div>
    </button>
  );
}
function BlogCard({ item, baseUrl, activeTab }: any) {
  const blogTheme = item?.attributes?.blog_theme;
  const isLight = blogTheme === "light";

  const titleColor = isLight ? "text-black" : "text-white";
  const descColor = isLight ? "text-black" : "text-white/90";
  const categoryColor = isLight ? "text-black" : "text-white";
  const diveInColor = isLight ? "text-black" : "text-white";
  const arrowInvert = isLight ? "invert" : "";
  const overlayClass = isLight
    ? "bg-white/30 group-hover:bg-white/60"
    : "bg-black/30 group-hover:bg-black/60";
  const blurOverlayClass = isLight ? "bg-white/40" : "bg-black/40";

  const imageUrl =
    item?.attributes?.["thumb-image"]?.data?.attributes?.url ||
    item?.attributes?.image?.data?.attributes?.url;

  const isWhitepaper =
    activeTab === "whitepaper" ||
    item?.attributes?.blog_types?.data?.some(
      (bt: any) => bt?.attributes?.slug === "whitepaper"
    ) ||
    item?.attributes?.article_type?.toLowerCase()?.includes("whitepaper");

  const cardHref =
    activeTab === "newsroom" ? "newsroom" : getArticleRoutePrefix(item?.attributes);

  return (
    <Link
      href={`/${cardHref}/${item?.attributes?.slug}`}
      data-blog-card-theme={isLight ? "light" : "dark"}
      className="blog-list-card-new group bg-[#0b0b0d] overflow-hidden cursor-pointer block w-full max-w-[400px] sm:max-w-[320px] md:max-w-[270px] lg:max-w-[320px] xl:max-w-[380px] 2xl:max-w-[430px] aspect-[27/41] mx-auto relative transition-transform duration-500"
    >
      <div className="relative h-full overflow-hidden pointer-events-none">
        {/* Image Wrapper */}
        <div className="absolute inset-0 overflow-hidden bg-[#0b0b0d]">
          {imageUrl && (
            <Image
              src={toWebp(`${baseUrl}${imageUrl}`)}
              alt={item?.attributes?.title || "Blog Image"}
              width={500}
              height={500}
              className="blog-card-image h-full group-hover:scale-150 transition-all duration-700 origin-top w-full object-cover object-center"
            />
          )}
          {/* Dynamic Overlay */}
          <div
            className={`blog-card-overlay absolute inset-0 ${overlayClass} transition-all duration-500`}
          />
        </div>

        {/* Blur Overlay */}
        <div
          className={`blog-card-blur absolute inset-0 ${blurOverlayClass} backdrop-blur-[10px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10`}
        />

        {/* Content */}
        <div className="relative z-20 flex flex-col justify-between h-full p-5">
          {/* Top Content */}
          <div className="transition-all duration-500 ">
            <span
              className={`inline-block ${categoryColor} text-[11px] mb-[10px] uppercase tracking-[1px] opacity-90`}
            >
              {item?.attributes?.article_type ||
                (isWhitepaper ? "Whitepaper" : "Blog")}
            </span>
            <h3
              className={`blog-card-title line-clamp-3 text-subtitle-2 font-semibold ${titleColor} transition-all duration-500 group-hover:scale-[1.05] origin-left group-hover:translate-y-[5px] leading-[1.3] group-hover:-translate-y-2`}
            >
              {item?.attributes?.list_title || item?.attributes?.title || "Untitled Post"}
            </h3>

            {/* Description appears below title on hover */}
            <div className="blog-card-desc mt-6 opacity-0 group-hover:opacity-100 translate-x-6 group-hover:translate-x-0 transition-all duration-700 delay-100">
              <p
                className={`${descColor} font-regular text-subtitle-5 leading-relaxed line-clamp-4`}
              >
                {(
                  item?.attributes?.list_content ||
                  item?.attributes?.content ||
                  "Explore our deep dive into enterprise technology and innovation, bringing you the latest expert-driven insights."
                ).replace(/<[^>]*>/g, "")}
              </p>
            </div>
          </div>

          {/* Bottom Button aligns to right */}
          <div className="blog-card-cta flex justify-end opacity-0 group-hover:opacity-100 transition-all duration-700 delay-300 transform translate-x-6 group-hover:translate-x-0">
            <div
              className={`inline-flex items-center gap-2 ${diveInColor} font-regular text-subtitle-5 group/btn`}
            >
              <span>Dive in</span>
              <Image
                src="/images/divn-in.svg"
                alt="divein"
                width={20}
                height={1}
                className={arrowInvert}
              />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

function DropdownContent({
  items,
  selectedSlugs = [],
  onItemClick,
  onClear,
  onShow,
}: {
  items: FilterOption[];
  selectedSlugs?: string[];
  onItemClick: (slug: string) => void;
  onClear: () => void;
  onShow: () => void;
}) {
  return (
    <div className="w-full animate-in fade-in duration-500 p-[25px_0px_25px_0px] bg-primary-text shadow-[0px_21px_45px_0px_#00000040,0px_82px_82px_0px_#00000036,0px_185px_111px_0px_#00000021,0px_328px_131px_0px_#0000000A,0px_513px_144px_0px_#00000000]">
      <div className="container">
        <div className="flex flex-wrap gap-3 md:gap-4">
          {items.map((item) => {
            const isSelected = selectedSlugs.includes(item.slug);
            return (
              <button
                key={item.id}
                onClick={() => onItemClick(item.slug)}
                className={`cursor-pointer px-[8px] md:px-[12px] py-[6px] md:py-[8px] text-subtitle-4 border transition-all duration-300 font-bold flex items-center gap-[6px] ${isSelected
                  ? "bg-[#7EAB00] text-white border-[#7EAB00]"
                  : "bg-[#32343B] text-gray-200 border-[#464953] hover:border-[#7EAB00] hover:text-white"
                  }`}
              >
                {isSelected && (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="flex-shrink-0">
                    <path d="M1.5 6L4.5 9L10.5 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
                {item.name}
              </button>
            );
          })}
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-end gap-6 md:gap-10 mt-[20px] md:mt-[30px]">
          <button
            onClick={onClear}
            className="cursor-pointer bg-transparent transition-all duration-300 p-[12px_20px] text-white text-subtitle-3 font-semibold flex items-center gap-2 group"
          >
            Clear all
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="mb-1 flex-[0_0_auto]">
              <path d="M1.25 9.6582H17.75" stroke="white" strokeWidth="1.5" strokeLinecap="square" strokeLinejoin="round" className="lg:group-hover:opacity-100 xl:opacity-0 opacity-100 transition-all duration-300"></path>
              <path d="M11.5908 2.5L18.7499 9.65909L11.5908 16.8181" stroke="white" strokeWidth="1.5" strokeLinecap="square" strokeLinejoin="round" className="lg:group-hover:translate-x-0 xl:-translate-x-[3px] -translate-x-0 transition-all duration-300"></path>
            </svg>
          </button>
          <button
            onClick={onShow}
            className="cursor-pointer bg-text-button hover:bg-blue-700 transition-all duration-300 p-[12px_20px] text-white text-subtitle-3 font-semibold flex items-center gap-2 group"
          >
            Show Results
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="my-[3px] flex-[0_0_auto]">
              <path d="M1.25 9.6582H17.75" stroke="white" strokeWidth="1.5" strokeLinecap="square" strokeLinejoin="round" className="lg:group-hover:opacity-100 xl:opacity-0 opacity-100 transition-all duration-300"></path>
              <path d="M11.5908 2.5L18.7499 9.65909L11.5908 16.8181" stroke="white" strokeWidth="1.5" strokeLinecap="square" strokeLinejoin="round" className="lg:group-hover:translate-x-0 xl:-translate-x-[3px] -translate-x-0 transition-all duration-300"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function BlogCardSkeleton() {
  return (
    <div
      className={`${styles["skeleton-card"]} w-full max-w-[400px] sm:max-w-[320px] md:max-w-[270px] lg:max-w-[320px] xl:max-w-[380px] 2xl:max-w-[430px] aspect-[27/41] mx-auto`}
    >
      <div>
        <div className={`${styles.skeleton} ${styles["skeleton-category"]}`} />
        <div className={`${styles.skeleton} ${styles["skeleton-title"]}`} />
        <div
          className={`${styles.skeleton} ${styles["skeleton-title-short"]}`}
        />
      </div>
      <div className="flex justify-end">
        <div
          className={`${styles.skeleton}`}
          style={{ width: "80px", height: "20px" }}
        />
      </div>
    </div>
  );
}

export default BlogPost;
